import React from 'react';

import type { OralExamRouteIds } from '@/common/hooks/oralExam/useOralExamRunStatePoll';
import type { OralExamRunStateResponse } from '@/common/api/models/oralExamRunStateResponse';

import { useOralExamMandatoryScreenShare } from '../hooks/useOralExamMandatoryScreenShare';
import { useOralExamMediaIntegrityMonitor } from '../hooks/useOralExamMediaIntegrityMonitor';
import type { StudentRuntimeJourney } from '../utils/deriveStudentRuntimeJourney';
import { OralExamStage, type OralExamStageProps } from './OralExamStage';

export type OralExamLiveViewProps = Omit<OralExamStageProps, 'runState'> & {
  runState: OralExamRunStateResponse;
  runtimeJourney?: StudentRuntimeJourney | null;
  routeIds?: OralExamRouteIds;
};

/**
 * Student-facing oral exam stage content. Delegates rendering to OralExamStage
 * and monitors mandatory screen share during official in-progress.
 */
export function OralExamLiveView({
  runState,
  bookingId,
  runtimeJourney,
  routeIds,
  ...rest
}: OralExamLiveViewProps) {
  useOralExamMandatoryScreenShare(runState);

  const {
    isScreenSharing,
    openSources,
    permissionDeniedSource,
    onReshareScreen,
    onEnableCamera,
    onUnmuteMic,
  } = useOralExamMediaIntegrityMonitor(bookingId, runState, routeIds);

  return (
    <OralExamStage
      runState={runState}
      bookingId={bookingId}
      runtimeJourney={runtimeJourney}
      isScreenSharing={isScreenSharing}
      openMediaSources={openSources}
      permissionDeniedSource={permissionDeniedSource}
      onReshareScreen={onReshareScreen}
      onEnableCamera={onEnableCamera}
      onUnmuteMic={onUnmuteMic}
      {...rest}
    />
  );
}
